import { Component } from '@theme/component';

/**
 * The "Buy More & Save" tier grid on the product page.
 *
 * Tiers come from a variant metafield rather than `variant.quantity_price_breaks`, which
 * Shopify only populates for logged-in B2B company customers. These prices are public, so
 * the discount itself is applied by a Shopify Function at checkout — this component is the
 * storefront half: it shows the ladder and drives the quantity.
 *
 * @typedef {object} Refs
 * @property {HTMLElement} [tiers] - Container for the tier buttons.
 * @property {HTMLElement[]} [tier] - Individual tier buttons.
 * @property {HTMLElement} [nudge] - "Add N more and save X%" prompt.
 * @property {HTMLElement} [nudgeCount] - The N in that prompt.
 * @property {HTMLElement} [nudgePercent] - The X in that prompt.
 * @property {HTMLInputElement} [quantity] - The panel's own quantity input.
 * @property {HTMLElement} [total] - Price shown on the submit button.
 * @property {HTMLElement} [showMore] - Toggle revealing tiers beyond the first row set.
 *
 * @extends {Component<Refs>}
 */
class CivitalVolumePricing extends Component {
  /** @type {{qty: number, price: number}[]} */
  #tiers = [];

  connectedCallback() {
    super.connectedCallback();

    this.#tiers = this.#parseTiers();
    this.addEventListener('click', this.#onClick);
    this.addEventListener('input', this.#onInput);
    this.#update();
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    this.removeEventListener('click', this.#onClick);
    this.removeEventListener('input', this.#onInput);
  }

  /** @returns {{qty: number, price: number}[]} Sorted ascending by quantity. */
  #parseTiers() {
    try {
      const raw = JSON.parse(this.dataset.tiers || '[]');

      return raw
        .map((t) => ({ qty: Number(t.qty), price: Number(t.price) }))
        .filter((t) => Number.isFinite(t.qty) && Number.isFinite(t.price) && t.qty > 0)
        .sort((a, b) => a.qty - b.qty);
    } catch (_) {
      return [];
    }
  }

  /** @returns {number} */
  get quantity() {
    return Math.max(1, Number(this.refs.quantity?.value ?? 1));
  }

  set quantity(value) {
    const next = Math.max(1, Math.floor(value));

    if (this.refs.quantity) this.refs.quantity.value = String(next);

    // Keep Horizon's own quantity input in step, so whichever button the shopper presses
    // adds the quantity they actually chose here rather than falling back to 1.
    const input = this.#themeQuantityInput();

    if (input) {
      input.value = String(next);
      input.dispatchEvent(new Event('input', { bubbles: true }));
      input.dispatchEvent(new Event('change', { bubbles: true }));
    }
  }

  /**
   * Horizon's own quantity field. Located by structure rather than by id: the add-to-cart
   * form carries no stable id, and more than one matches the cart-add action, so guessing
   * silently targets the wrong element.
   *
   * @returns {HTMLInputElement | null}
   */
  #themeQuantityInput() {
    for (const candidate of document.querySelectorAll('input[name="quantity"]')) {
      if (!(candidate instanceof HTMLInputElement)) continue;
      if (candidate === this.refs.quantity) continue;
      if (candidate.closest('civital-volume-pricing')) continue;

      return candidate;
    }

    return null;
  }

  /** @returns {HTMLFormElement | null} The form that actually owns the quantity field. */
  #productForm() {
    const input = this.#themeQuantityInput();

    if (input?.form) return input.form;

    return document.querySelector('form[action*="/cart/add"]');
  }

  /** Submit the theme's real product form, so Horizon's cart handling still runs. */
  #submit() {
    const form = this.#productForm();

    if (!form) return;

    const submitter = form.querySelector('[type="submit"]');

    if (submitter instanceof HTMLElement) {
      submitter.click();
    } else {
      form.requestSubmit();
    }
  }

  /**
   * The best tier for a quantity: the highest tier whose threshold is met.
   *
   * @param {number} qty
   * @returns {{qty: number, price: number} | null}
   */
  #tierFor(qty) {
    let match = null;

    for (const tier of this.#tiers) {
      if (qty >= tier.qty) match = tier;
    }

    return match;
  }

  /**
   * @param {number} qty
   * @returns {{qty: number, price: number} | null} The next tier up, if any.
   */
  #nextTier(qty) {
    for (const tier of this.#tiers) {
      if (tier.qty > qty) return tier;
    }

    return null;
  }

  /**
   * @param {number} amount
   * @returns {string}
   */
  #money(amount) {
    const currency = this.dataset.currency || 'GBP';

    return new Intl.NumberFormat(document.documentElement.lang || 'en-GB', {
      style: 'currency',
      currency,
    }).format(amount);
  }

  /** @param {Event} event */
  #onClick = (event) => {
    const target = event.target;

    if (!(target instanceof Element)) return;

    const tierButton = target.closest('[data-tier-qty]');

    if (tierButton instanceof HTMLElement) {
      event.preventDefault();
      this.quantity = Number(tierButton.dataset.tierQty);
      this.#update();
      return;
    }

    const step = target.closest('[data-step]');

    if (step instanceof HTMLElement) {
      event.preventDefault();
      this.quantity = this.quantity + Number(step.dataset.step);
      this.#update();
      return;
    }

    if (target.closest('[data-show-more]')) {
      event.preventDefault();
      this.classList.toggle('civital-volume--expanded');
      return;
    }

    if (target.closest('[data-submit]')) {
      event.preventDefault();
      this.quantity = this.quantity;
      this.#submit();
    }
  };

  #onInput = (event) => {
    if (event.target === this.refs.quantity) this.#update();
  };

  #update() {
    const qty = this.quantity;
    const base = this.#tiers[0]?.price ?? Number(this.dataset.basePrice || 0);
    const tier = this.#tierFor(qty);
    const unit = tier ? tier.price : base;

    // The headline price is the ladder's range and is rendered once, in Liquid. Only the
    // button follows the quantity.
    if (this.refs.total) this.refs.total.textContent = this.#money(unit * qty);

    for (const button of this.querySelectorAll('[data-tier-qty]')) {
      const isActive = tier != null && Number(button.getAttribute('data-tier-qty')) === tier.qty;
      button.classList.toggle('civital-volume__tier--active', isActive);
      button.setAttribute('aria-pressed', String(isActive));
    }

    const next = this.#nextTier(qty);
    const { nudge, nudgeCount, nudgePercent } = this.refs;

    if (nudge) {
      if (next && base > 0) {
        const saving = Math.round(((base - next.price) / base) * 100);

        if (nudgeCount) nudgeCount.textContent = String(next.qty - qty);
        if (nudgePercent) nudgePercent.textContent = String(saving);
        nudge.hidden = saving <= 0;
      } else {
        nudge.hidden = true;
      }
    }
  }
}

if (!customElements.get('civital-volume-pricing')) {
  customElements.define('civital-volume-pricing', CivitalVolumePricing);
}
